import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, allComics, reviews } from '@/mocks/comics';
import SchemaInjector from '@/components/base/SchemaInjector';
import { getOrganizationSchema, getWebPageSchema, getBreadcrumbSchema, getFAQSchema } from '@/mocks/schema';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const schemas = [
    getOrganizationSchema(),
    getWebPageSchema({
      title: '关于我们 - 韩漫之家 | 韩国漫画免费阅读平台',
      description: '了解韩漫之家品牌故事，平台数据，商务合作。韩漫之家是国内领先的韩国漫画免费阅读平台，收录5000+部韩漫作品。',
      url: 'http://informyi.com/about',
    }),
    getBreadcrumbSchema([
      { name: '首页', url: 'http://informyi.com/' },
      { name: '关于我们', url: 'http://informyi.com/about' },
    ]),
    getFAQSchema([
      { question: '韩漫之家是否需要付费？', answer: '韩漫之家所有漫画均为免费阅读，无需任何付费即可畅享全部内容。' },
      { question: '韩漫之家的漫画更新频率如何？', answer: '我们与韩国同步更新，确保用户第一时间读到最新话的翻译版本。' },
      { question: '可以在手机上阅读吗？', answer: '韩漫之家支持电脑、平板和手机等多设备阅读，自适应屏幕尺寸，随时随地畅享阅读。' },
      { question: '韩漫之家收录了多少部漫画？', answer: '平台已收录超过5000部优质韩国漫画作品，涵盖恋爱、奇幻、剧情、惊悚等各类热门题材。' },
    ]),
  ];

  const features = [
    { icon: 'ri-book-read-line', title: '海量资源', desc: '收录超过5000部韩国漫画，涵盖恋爱、奇幻、剧情、惊悚等各类题材' },
    { icon: 'ri-flashlight-line', title: '极速更新', desc: '与韩国同步更新，第一时间提供最新话翻译，让您追更不等待' },
    { icon: 'ri-smartphone-line', title: '多端阅读', desc: '支持电脑、平板、手机等多设备阅读，自适应屏幕尺寸' },
    { icon: 'ri-shield-check-line', title: '高清画质', desc: '所有漫画均为高清扫描版本，画质细腻清晰，阅读体验一流' },
  ];

  const stats = [
    { value: '5000+', label: '收录作品', icon: 'ri-stack-line' },
    { value: '8', label: '内容分类', icon: 'ri-layout-grid-line' },
    { value: '99%', label: '用户满意度', icon: 'ri-heart-line' },
    { value: '1.2亿+', label: '累计阅读量', icon: 'ri-eye-line' },
    { value: '24h', label: '同步更新', icon: 'ri-time-line' },
    { value: '100万+', label: '活跃读者', icon: 'ri-user-star-line' },
  ];

  const milestones = [
    { year: '2018', title: '平台创立', desc: '韩漫之家正式上线，从零开始搭建漫画阅读平台' },
    { year: '2019', title: '内容爆发', desc: '收录漫画突破1000部，月活用户突破10万' },
    { year: '2020', title: '技术升级', desc: '全面升级阅读器体验，支持高清画质和多端适配' },
    { year: '2021', title: '同步更新', desc: '实现与韩国同步更新，保证用户第一时间阅读最新内容' },
    { year: '2022', title: '内容生态', desc: '拓展多元内容，新增搞笑、日常、历史等垂类频道' },
    { year: '2023', title: '用户破百万', desc: '月活跃用户突破100万，成为国内领先的韩漫平台' },
    { year: '2024', title: '品质升级', desc: 'AI智能推荐上线，阅读体验全面优化，用户满意度达99%' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('loading');
    setFormError('');
    try {
      const response = await fetch(form.getAttribute('action') || '', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(parsed?.meta?.message || parsed?.meta?.detail || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <SchemaInjector schema={schemas} />
      <Navbar />
      <main>
        <section className="relative w-full h-[350px] md:h-[480px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20panoramic%20illustration%20of%20a%20grand%20modern%20library%20filled%20with%20colorful%20comic%20books%20and%20reading%20spaces%2C%20warm%20ambient%20lighting%20from%20large%20windows%2C%20cozy%20and%20inviting%20atmosphere%2C%20artistic%20composition%2C%20soft%20pastel%20colors%2C%20cinematic%20quality&width=1600&height=500&seq=about-hero-01&orientation=landscape"
            alt="关于韩漫之家"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/40"></div>
          <div className="absolute inset-0 z-10 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="lg:w-2/5 text-center lg:text-left">
                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-3">
                      关于我们
                    </h1>
                    <p className="text-base text-background-50/80 leading-relaxed max-w-md mx-auto lg:mx-0">
                      韩漫之家，让每一位漫画爱好者都能轻松享受韩国漫画的魅力
                    </p>
                  </div>
                  <div className="hidden lg:block lg:w-3/5"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <nav className="bg-background-50 border-b border-background-200/70">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto py-3">
              <ol className="flex items-center gap-2 text-xs text-foreground-500">
                <li><Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-foreground-950 font-medium">关于我们</li>
              </ol>
            </div>
          </div>
        </nav>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <p className="text-sm font-semibold tracking-widest text-foreground-400 mb-2 uppercase">品牌故事</p>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                  我们<span className="italic text-secondary-500">为什么</span>做韩漫之家
                </h2>
                <p className="text-sm text-foreground-500 max-w-lg mx-auto">源于对漫画的热爱，始于对品质的追求</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                <div className="bg-secondary-500/10 rounded-2xl p-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50 mb-5">
                    <i className="ri-heart-pulse-line text-secondary-500 text-xl"></i>
                  </div>
                  <p className="text-5xl md:text-6xl font-heading font-bold text-foreground-950 mb-3">99%</p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    用户满意度高达99%，我们用心服务每一位漫画爱好者，持续优化阅读体验。
                  </p>
                  <p className="text-xs italic text-foreground-500 mt-4">品质源于专注</p>
                </div>
                <div className="bg-accent-100/60 rounded-2xl p-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50 mb-5">
                    <i className="ri-stack-fill text-accent-500 text-xl"></i>
                  </div>
                  <p className="text-5xl md:text-6xl font-heading font-bold text-foreground-950 mb-3">{allComics.length}+</p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    平台收录{allComics.length}+部优质韩国漫画，覆盖{categories.length}大内容分类，且每日持续更新新作品，满足您多元化的阅读需求。
                  </p>
                  <p className="text-xs italic text-foreground-500 mt-4">海量资源 · 持续更新</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">平台数据</h2>
                <p className="text-foreground-600 text-sm max-w-lg mx-auto">用数据说话，韩漫之家的实力见证</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {stats.map((stat) => (
                  <div key={stat.label} className="bg-background-100 rounded-xl p-5 text-center">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-3">
                      <i className={`${stat.icon} text-lg`}></i>
                    </div>
                    <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">{stat.value}</p>
                    <p className="text-xs text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">我们的优势</h2>
                <p className="text-foreground-600 text-sm max-w-lg mx-auto">为什么选择韩漫之家</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {features.map((feat) => (
                  <div key={feat.title} className="bg-background-50 rounded-xl p-5 text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
                      <i className={`${feat.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-bold text-foreground-950 mb-2">{feat.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{feat.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row items-start gap-10">
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">用户口碑</span>
                  </div>
                  <blockquote className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                    &ldquo;韩漫之家改变了我对在线漫画阅读的认知&rdquo;
                  </blockquote>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    我们始终坚持以用户为中心，不断优化产品体验，让更多漫画爱好者能够便捷地享受优质韩漫内容。
                  </p>
                  <div className="space-y-3">
                    {reviews.map((r) => (
                      <div key={r.id} className="bg-background-100 rounded-xl p-4">
                        <div className="flex items-center gap-1 mb-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i key={i} className={`text-xs ${i < r.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                          ))}
                        </div>
                        <p className="text-sm text-foreground-700 mb-1">&ldquo;{r.text}&rdquo;</p>
                        <span className="text-xs text-foreground-500">&mdash; {r.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">发展历程</span>
                  </div>
                  <div className="space-y-1">
                    {milestones.map((m, idx) => (
                      <div key={m.year} className="flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors">
                        <div className="shrink-0 w-14 text-right">
                          <span className="text-sm font-bold text-primary-500">{m.year}</span>
                        </div>
                        <div className={`${idx < milestones.length - 1 ? 'border-l-2 border-background-200/70' : ''} pl-4 pb-4`}>
                          <h4 className="text-sm font-bold text-foreground-950">{m.title}</h4>
                          <p className="text-xs text-foreground-500 mt-0.5">{m.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">商务合作</h2>
                <p className="text-foreground-600 text-sm max-w-lg mx-auto">欢迎各类合作洽谈，共建韩漫生态</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background-50 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-primary-100 text-primary-700 mb-4">
                    <i className="ri-group-line text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-950 mb-2">内容合作</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    欢迎韩国漫画版权方、翻译团队与我们合作，共同为中文读者带来更多优质韩漫内容。
                  </p>
                </div>
                <div className="bg-background-50 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-accent-100 text-accent-700 mb-4">
                    <i className="ri-advertisement-line text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-950 mb-2">广告合作</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    平台拥有百万级月活跃用户，欢迎品牌方洽谈广告投放、品牌联名等合作形式。
                  </p>
                </div>
                <div className="bg-background-50 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-700 mb-4">
                    <i className="ri-briefcase-line text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-950 mb-2">商务洽谈</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    如果您有其他合作想法或建议，欢迎通过下方表单与我们联系，我们将尽快回复。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="relative w-full h-[380px] md:h-[480px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20warm%20inviting%20illustration%20of%20a%20cozy%20reading%20corner%20with%20bookshelves%20and%20soft%20lamplight%2C%20golden%20hour%20sunlight%20streaming%20through%20window%2C%20peaceful%20atmosphere%2C%20horizontal%20composition%2C%20artistic%20style%2C%20soft%20warm%20color%20palette&width=1600&height=600&seq=cta-about-01&orientation=landscape"
            alt="联系我们"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/50"></div>
          <div className="absolute inset-0 z-10 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto relative h-full">
                <div className="absolute top-1/4 left-0 md:left-4">
                  <h2 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-background-50 leading-tight">
                    联系我们
                  </h2>
                </div>
                <div className="absolute bottom-1/4 right-0 md:right-4 max-w-xs">
                  <p className="text-base md:text-lg text-background-50/80 leading-relaxed font-light">
                    有任何问题或建议？<br />
                    欢迎随时与我们联系
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-8">
                给我们留言
              </h2>

              {formStatus === 'success' ? (
                <div className="bg-accent-100 border border-accent-200 rounded-xl p-8 text-center">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-500 text-background-50 mb-4">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-foreground-950 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-600">感谢您的留言，我们会尽快回复您。</p>
                </div>
              ) : (
                <form
                  action="http://informyi.com/"
                  method="POST"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                 
                >
                  <div className="honeypot-field">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                    >
                      <option value="">请选择留言主题</option>
                      <option value="内容合作">内容合作</option>
                      <option value="广告合作">广告合作</option>
                      <option value="商务洽谈">商务洽谈</option>
                      <option value="意见反馈">意见反馈</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      maxLength={500}
                      className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>

                  {formStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full bg-primary-500 text-background-50 py-3.5 rounded-full font-semibold text-sm whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer"
                  >
                    {formStatus === 'loading' ? '提交中...' : '提交留言'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}